import React from 'react';
import { FeatureCard } from './Pricing';
import { Avatar, Box, Divider, Stack, Typography } from '@mui/material';
import { ReactComponent as GemstoneMatching } from '../../assets/images/gemstone-matching.svg';
import { ReactComponent as GemstoneRecommendation } from '../../assets/images/gemstone-gemstone.svg';
import { ReactComponent as GemstoneCertification } from '../../assets/images/gemstone-certified.svg';
import { ReactComponent as GemstoneRitual } from '../../assets/images/gemstone-ritual.svg';
import Ruby from '../../assets/images/ruby.svg';
import Pearl from '../../assets/images/pearl.svg';
import RedCoral from '../../assets/images/redcoral.svg';
import Emerald from '../../assets/images/emerald.svg';
import YellowSapphire from '../../assets/images/yellowsapphire.svg';
import Diamond from '../../assets/images/diamond.svg';
import BlueSapphire from '../../assets/images/bluesapphire.svg';
import Hessonite from '../../assets/images/hessonite.svg';
import CatsEye from '../../assets/images/catseye.svg';
import { YellowBoxBottom } from './Home';

const processSteps = [
  {
    title: 'Astrological Matching',
    description:
      'We analyze your chart to identify planetary strengths, afflictions and current Dasha periods.',
    icon: GemstoneMatching,
  },
  {
    title: 'Gemstone Recommendation',
    description:
      'Based on Astrological matching, we suggest suitable stones, their weight, metal & finger of wearing.',
    icon: GemstoneRecommendation,
  },
  {
    title: 'Certified Authenticity',
    description:
      'We provide government approved Lab certified gemstones only IGI  GIA',
    icon: GemstoneCertification,
  },
  {
    title: 'Ritual Guidelines',
    description:
      'You get a complete Puja + mantra set of instructions to activate the gemstone before wearing.',
    icon: GemstoneRitual,
  },
];

const gemstones = [
  {
    name: 'Ruby (Manikya)',
    icon: Ruby,
    description:
      'Represents the Sun. Brings confidence, leadership, vitality, and fame. Ideal for those with weak or afflicted Sun in their charts.',
  },
  {
    name: 'Pearl (Moti)',
    icon: Pearl,
    description:
      'Represents the Moon. Enhances emotional balance, peace, and mental stability. Beneficial for anxiety, stress, and insomnia.',
  },
  {
    name: 'Red Coral (Moonga)',
    icon: RedCoral,
    description:
      'Ruled by Mars. Increases courage, energy, and determination. Great for those facing lethargy, anger issues, or blood-related ailments.',
  },
  {
    name: 'Emerald (Panna)',
    icon: Emerald,
    description:
      'Ruled by Mercury. Boosts intellect, communication, and business success. Aids memory and learning.',
  },
  {
    name: 'Yellow Sapphire (Pukhraj)',
    icon: YellowSapphire,
    description:
      'Represents Jupiter. Brings wisdom, prosperity, and spiritual growth. Ideal for those seeking guidance and abundance.',
  },
  {
    name: 'Diamond (Heera)',
    icon: Diamond,
    description:
      'Symbolises Venus. Enhances charm, creativity, luxury, and relationships.',
  },
  {
    name: 'Blue Sapphire (Neelam)',
    icon: BlueSapphire,
    description:
      'Represents Saturn. Can be life-changing, brings discipline, structure, and wealth when suited.',
  },
  {
    name: 'Hessonite (Gomed)',
    icon: Hessonite,
    description:
      'Linked to Rahu. Removes confusion, addiction, and negativity. Ideal during Rahu Dasha.',
  },
  {
    name: 'Cat’s Eye (Lehsunia)',
    icon: CatsEye,
    description:
      'Represents Ketu. A stone of spiritual power, detachment, and protection from hidden enemies.',
  },
];

const Gemstone = () => {
  return (
    <Box>
      <Typography variant="homePageBody" paragraph>
        We’re building a trusted ecosystem for authentic, ethically sourced
        gemstones. In an unregulated and often deceptive market, we aim to be a
        beacon of integrity providing only certified, lab-tested stones that
        align with your astrological profile. Each recommendation is backed by
        deep Vedic astrology analysis, not mass-market guesswork.
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        Why Gemstones?
      </Typography>
      <Typography variant="homePageBody" paragraph>
        In Vedic astrology, gemstones are not mere accessories they are powerful
        cosmic tools. Each stone carries planetary vibrations that can enhance
        your strengths, balance imbalances, and protect you from negative
        energies. But their effect is only real when they are:
        <br />
        <br />• Chosen correctly
        <br />• Worn at the right time
        <br />• Activated with proper rituals
        <br />
        <br />
        This is why consultation is essential. Wearing the wrong stone without
        guidance can sometimes worsen planetary afflictions instead of improving
        them.
      </Typography>
      <Typography variant="h2" sx={{ marginTop: '100px' }}>
        Our Process
      </Typography>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
        gap={4}
        justifyContent="center"
        alignItems="start"
      >
        {processSteps.map((step, i) => (
          <FeatureCard key={i} {...step} Icon={step.icon} />
        ))}
      </Box>
      <Divider />
      <Typography variant="h2">Explore the Power of Each Gemstone</Typography>
      <Typography variant="homePageBody" paragraph>
        Here’s a quick guide to the most commonly recommended Vedic gemstones:
      </Typography>
      <Stack spacing={4} my={5}>
        {gemstones.map((gem, idx) => (
          <Stack
            key={idx}
            direction="column"
            spacing={2}
            alignItems="flex-start"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h3">{gem.name}</Typography>
              <Avatar
                src={gem.icon}
                alt={gem.name}
                sx={{
                  width: 40,
                  height: 40,
                  mt: '2px',
                  bgcolor: 'transparent',
                }}
                variant="rounded"
              />
            </Stack>
            <Box>
              <Typography color="text.secondary" mt={0.5}>
                {gem.description}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
      <Divider />
      <Typography variant="h3" fontWeight={600}>
        Our Vision for Gemstones
      </Typography>
      <Typography variant="homePageBody" paragraph>
        The gemstone industry today is one of the most unregulated and
        misunderstood segments within spiritual and astrological ecosystems.
        Countless individuals are misled with fake, low-grade, or energetically
        ineffective stones leading not only to financial loss but spiritual
        disillusionment.
        <br />
        <br />
        We envision a future where gemstones are no longer just commercial items
        but respected tools of transformation, verified by both spiritual
        authenticity and scientific accuracy.
      </Typography>
      <YellowBoxBottom>
        <Typography variant="h3" fontWeight={600}>
          Consult before you wear. Trust before you buy.
        </Typography>
        <Box sx={{ marginLeft: '36px', marginRight: '36px' }}>
          <Typography variant="homePageBody" textAlign={'center'}>
            Gemstones are sacred. They are not fashion statements but spiritual
            accelerators and must be treated as such. With{' '}
            <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>,
            you're not just buying a stone you're entering a sacred,
            science-backed journey toward alignment and growth.
          </Typography>
        </Box>
      </YellowBoxBottom>
    </Box>
  );
};

export default Gemstone;
